import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "../components/PageShell";
import PageHero from "../components/PageHero";

const ROLES = [
  {
    key: "senior-fs",
    title: "Senior Full-stack Engineer",
    location: "Remote · EU / UK timezone",
    type: "Full-time",
    blurb:
      "Type-safe TypeScript, Postgres, and a strong product instinct. You'll lead engagements end-to-end.",
  },
  {
    key: "ai-eng",
    title: "AI Systems Engineer",
    location: "Remote · Worldwide",
    type: "Full-time",
    blurb:
      "Production agents, RAG, eval pipelines. You've shipped at least one LLM-powered product that real users rely on.",
  },
  {
    key: "appsec",
    title: "Application Security Engineer",
    location: "Lisbon · Hybrid",
    type: "Full-time",
    blurb:
      "Threat modelling, pen-testing, SOC2 hardening. Comfortable owning security across multiple client engagements.",
  },
  {
    key: "design",
    title: "Senior Product Designer",
    location: "Remote · EU / UK timezone",
    type: "Full-time",
    blurb:
      "You design and ship — Figma to production-grade React. Premium craft, opinionated taste.",
  },
];

const PRINCIPLES = [
  ["No bench, no idle time.", "We don't keep people around between projects. Everyone is on a real engagement."],
  ["Senior-only.", "The bar is shipping something hard before. If you've done it, you'll fit. If not, we're not the right first job."],
  ["Async by default.", "We work across 5 timezones. Writing is the primary medium. Meetings are short and rare."],
  ["Open compensation.", "Salary bands are public internally. No surprises."],
];

export default function CareersPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="◇ Careers"
        title="Senior engineers, mostly."
        accent="mostly."
        kicker="We hire rarely and we hire deeply. If you've spent a decade shipping software that has to work, you'll probably enjoy it here."
      />

      {/* How we hire */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ How we hire
            </span>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight text-primary-x">
              Three conversations. <br /> A paid trial. That&apos;s it.
            </h2>
          </div>
          <ol className="md:col-span-7 space-y-6 text-secondary-x leading-relaxed">
            {[
              ["01", "Intro call", "20 min — get to know each other, talk about what you've built."],
              ["02", "Working session", "90 min — we dig into a real problem we're working on. Paid."],
              ["03", "Studio fit", "60 min with two principals — culture, principles, expectations."],
              ["04", "Paid trial", "Two weeks on a live engagement. If it clicks, you join. If it doesn't, you keep the fee and we stay friends."],
            ].map(([n, h, d]) => (
              <li key={n} className="grid grid-cols-[44px_1fr] gap-4 items-baseline">
                <span className="font-mono-x text-xs text-muted-x">{n}</span>
                <div>
                  <div className="text-primary-x font-medium">{h}</div>
                  <div className="text-secondary-x mt-1">{d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Roles */}
      <section className="py-24 md:py-32 bg-surface border-y border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-12">
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ Open roles
            </span>
            <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] text-primary-x">
              We&apos;re hiring for <em className="not-italic text-muted-x">{ROLES.length}.</em>
            </h2>
          </div>
          <div className="divide-y divide-hairline border-y border-hairline">
            {ROLES.map((r, i) => (
              <motion.div
                key={r.key}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.05, duration: 0.6 }}
                data-testid={`role-${r.key}`}
                className="group py-8 grid md:grid-cols-12 gap-6 items-center"
              >
                <div className="md:col-span-6">
                  <h3 className="font-display text-2xl md:text-4xl font-medium tracking-tight text-primary-x group-hover:opacity-90 transition-opacity">
                    {r.title}
                  </h3>
                  <p className="mt-3 text-secondary-x max-w-xl">{r.blurb}</p>
                </div>
                <div className="md:col-span-4 flex flex-wrap items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
                  <span>{r.location}</span>
                  <span className="h-px w-6 bg-hairline-strong" />
                  <span>{r.type}</span>
                </div>
                <div className="md:col-span-2 md:text-right">
                  <Link
                    to="/contact"
                    data-testid={`role-${r.key}-apply`}
                    className="inline-flex items-center gap-2 text-primary-x text-sm group/cta"
                  >
                    Apply
                    <span className="transition-transform group-hover/cta:translate-x-0.5">→</span>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 text-secondary-x text-sm">
            Don&apos;t see your role? Write to <a href="mailto:careers@ulties.com" className="text-primary-x hover:underline">careers@ulties.com</a> anyway. If you&apos;ve built something hard, we want to hear.
          </p>
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-12">
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ How we operate
            </span>
            <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] text-primary-x">
              The four rules.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PRINCIPLES.map(([t, d]) => (
              <div
                key={t}
                className="rounded-2xl border border-hairline bg-glass p-8"
              >
                <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
                  {t}
                </h3>
                <p className="mt-4 text-secondary-x leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
